import React from 'react';
import { Col, Grid as BaseGrid, Row } from 'react-styled-flexboxgrid';
import { withFormik } from 'formik';
import { withToastManager } from 'react-toast-notifications';
import * as Yup from 'yup';
import { default as BaseInput } from '../../../components/Input';
import { default as BaseButton } from 'reusecore/src/elements/Button';
import useMediaQuery from '../../../helpers/mediaQuery';
import { default as BaseProContactMobileIcon } from './ProContactMobileIcon';
import { useTranslation, withTranslation } from 'next-i18next';
import FormGroup from 'reusecore/src/elements/FormGroup';
import styled from 'styled-components';
import { isValidPhoneNumber } from 'react-phone-number-input';
import { Counter } from '../../../helpers/Counter';
import { db } from '../../../helpers/firebaseHelper';

const validationSchema = Yup.object().shape({
  name: Yup.string().required('validation.missing'),
  firstname: Yup.string().required('validation.missing'),
  email: Yup.string()
    .required('validation.missing')
    .email('validation.mail.invalid'),
  phone: Yup.string().test({
    name: 'validPhoneNumber',
    message: 'validation.mobile.invalid',
    test: value => (!!value ? isValidPhoneNumber(value) : true),
  }),
  message: Yup.string().required('validation.missing'),
});

const ProContactMobileIcon = styled(BaseProContactMobileIcon)`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 30px 0;
`;

const Legal = styled.p`
  color: #595959;
`;

const Button = styled(BaseButton)`
  width: 100%;
`;

const StyledInput = styled(BaseInput)`
  label {
    top: -25px !important;
    @media (max-width: 600px) {
      top: -20px !important;
    }
  }
  input,
  textarea,
  select {
    min-height: 50px;
    border-radius: 4px;
    border-width: 1px;
    border-style: solid;
    border-color: rgba(11, 47, 63, 0.2);
    background-color: #ffffff;
    color: #0b2f3f;
    padding: 15px 0 15px 15px;
    font: 'Lato Bold';
  }
  textarea {
    resize: none;
    max-width: 100%;
  }
`;

const Input = props => {
  return <StyledInput {...props} />;
};

const Form = styled.form`
  max-width: 100%;
`;

const Grid = styled(BaseGrid)`
  padding-right: 15px;
  padding-left: 15px;
  max-width: 100%;
`;

const DEFAULT_VALUES = {
  name: '',
  firstname: '',
  email: '',
  phone: '',
  message: '',
};

const ContactForm = props => {
  let {
    values,
    touched,
    errors,
    handleChange,
    handleBlur,
    handleSubmit,
    isSubmitting,
    isTablet,
    proUser,
  } = props;

  const { t } = useTranslation();

  const isMobile = useMediaQuery('(max-width: 1023px)');

  const name = `${t('pro.contact.name')}*`;
  const firstname = `${t('pro.contact.firstname')}*`;
  const email = `${t('pro.contact.email')}*`;
  const phone = `${t('pro.contact.phone')}`;
  const message = `${t('pro.contact.message')}*`;

  return (
    <Form onSubmit={handleSubmit}>
      <Grid>
        <Row style={{ marginBottom: 20 }}>
          <Col style={{ marginBottom: isMobile ? 20 : 0 }} xs={12} md={6}>
            <FormGroup className={errors.name ? 'hasErrorMsg' : ''}>
              <Input
                label={name}
                elementType="input"
                changed={handleChange('name')}
                value={values.name}
                elementConfig={{
                  name: 'name',
                  onBlur: handleBlur('name'),
                  value: values.name,
                  placeholder: t('pro.contact.name'),
                }}
              />
              <span className="errorMsg">
                {(errors.name && touched.name) || (isSubmitting && errors.name)
                  ? t(errors.name)
                  : null}
              </span>
            </FormGroup>
          </Col>
          <Col xs={12} md={6}>
            <FormGroup className={errors.firstname ? 'hasErrorMsg' : ''}>
              <Input
                label={firstname}
                elementType="input"
                changed={handleChange('firstname')}
                value={values.firstname}
                elementConfig={{
                  name: 'firstname',
                  onBlur: handleBlur('firstname'),
                  value: values.firstname,
                  placeholder: t('pro.contact.firstname'),
                }}
              />
              <span className="errorMsg">
                {(errors.firstname && touched.firstname) ||
                (isSubmitting && errors.firstname)
                  ? t(errors.firstname)
                  : null}
              </span>
            </FormGroup>
          </Col>
        </Row>
        <Row style={{ marginBottom: 20 }}>
          <Col style={{ marginBottom: isMobile ? 20 : 0 }} xs={12} md={6}>
            <FormGroup className={errors.email ? 'hasErrorMsg' : ''}>
              <Input
                label={email}
                elementType="input"
                changed={handleChange('email')}
                value={values.email}
                elementConfig={{
                  type: 'email',
                  name: 'email',
                  onBlur: handleBlur('email'),
                  value: values.email,
                  placeholder: 'example@gmail.com',
                }}
              />
              <span className="errorMsg">
                {(errors.email && touched.email) ||
                (isSubmitting && errors.email)
                  ? t(errors.email)
                  : null}
              </span>
            </FormGroup>
          </Col>
          <Col xs={12} md={6}>
            <FormGroup className={errors.phone ? 'hasErrorMsg' : ''}>
              <Input
                label={phone}
                elementType="phone"
                changed={handleChange('phone')}
                value={values.phone}
                elementConfig={{
                  type: 'phone',
                  name: 'phone',
                  onBlur: handleBlur('phone'),
                  value: values.phone,
                }}
              />
              <span className="errorMsg">
                {(errors.phone && touched.phone) ||
                (isSubmitting && errors.phone)
                  ? t(errors.phone)
                  : null}
              </span>
            </FormGroup>
          </Col>
        </Row>
        <Row>
          <Col xs={12}>
            <FormGroup className={errors.message ? 'hasErrorMsg' : ''}>
              <Input
                label={message}
                elementType="textarea"
                changed={handleChange('message')}
                value={values.message}
                minRows={6}
                maxRows={6}
                rows="auto"
                elementConfig={{
                  name: 'message',
                  onBlur: handleBlur('message'),
                  value: values.message,
                  placeholder: t('pro.contact.messagePlaceholder'),
                }}
              />
              <span className="errorMsg">
                {(errors.message && touched.message) ||
                (isSubmitting && errors.message)
                  ? t(errors.message)
                  : null}
              </span>
            </FormGroup>
          </Col>
        </Row>
        <Row>
          <Col xs={12} md={3}>
            <Button
              title={t('pro.contact.submit')}
              type="submit"
              isLoading={isSubmitting}
              disabled={isSubmitting}
              loaderColor="#fff"
            />
          </Col>
        </Row>
        {isTablet ? (
          <Row>
            <Col xs={12}>
              <ProContactMobileIcon />
            </Col>
          </Row>
        ) : null}
        <Row>
          <Col xs={12}>
            <Legal>{t('pro.contact.legal')}</Legal>
          </Col>
        </Row>
      </Grid>
    </Form>
  );
};

export default withTranslation()(
  withToastManager(
    withFormik({
      mapPropsToValues: () => DEFAULT_VALUES,
      validationSchema,
      handleSubmit: (
        values,
        { setSubmitting, props, resetForm, setValues }
      ) => {
        setSubmitting(true);
        const {
          proUser: {
            address: { email },
            uid,
          },
          toastManager,
          t,
        } = props;

        const params = new URLSearchParams({ ...values, to: email });

        fetch(process.env.FUNCTION_PRO_CONTACT, {
          method: 'POST',
          body: params,
        })
          .then(response => {
            setSubmitting(false);
            if (!response.ok) {
              toastManager.add(t('pro.contact.error'), {
                appearance: 'error',
                autoDismiss: true,
              });
            } else {
              toastManager.add(t('pro.contact.sent'), {
                appearance: 'success',
                autoDismiss: true,
              });
              resetForm();

              // Initialize the sharded counter.
              const contacts = new Counter(
                db.collection('users').doc(uid),
                'stats.contacts'
              );
              // Increment the field "contacts" of the document
              contacts.incrementBy(1);
            }
          })
          .catch(e => {
            setSubmitting(false);
            toastManager.add(t('pro.contact.error'), {
              appearance: 'error',
              autoDismiss: true,
            });
          });
      },
    })(ContactForm)
  )
);
